import { useState, useEffect } from 'react'
import { Product, STORES, CATEGORIES, StorePrice } from '../types'

interface ProductFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (product: Product) => Promise<void>
  product?: Product
  isEditing?: boolean
}

const ProductForm = ({ isOpen, onClose, onSave, product, isEditing = false }: ProductFormProps) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    prices: product?.prices || [] as StorePrice[],
    category: product?.category || 'Muu',
    isFavorite: product?.isFavorite || false,
    rotationDays: product?.rotationDays || undefined
  })
  
  const [isLoading, setIsLoading] = useState(false)

  // Update form data when product prop changes
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        prices: product.prices || [],
        category: product.category,
        isFavorite: product.isFavorite,
        rotationDays: product.rotationDays || undefined
      })
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        prices: [],
        category: 'Muu',
        isFavorite: false,
        rotationDays: undefined
      })
    }
  }, [product])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      alert('Tuotteen nimi on pakollinen')
      return
    }
    
    if (formData.prices.length === 0) {
      alert('Ainakin yksi hinta täytyy määrittää')
      return
    }

    const validPrices = formData.prices.filter(p => p.price > 0)
    if (validPrices.length === 0) {
      alert('Ainakin yksi hinta täytyy olla suurempi kuin 0')
      return
    }

    setIsLoading(true)
    try {
      await onSave({
        name: formData.name.trim(),
        prices: validPrices,
        category: formData.category,
        isFavorite: formData.isFavorite,
        rotationDays: formData.rotationDays || undefined
      })
      onClose()
    } catch (error) {
      console.error('Form save error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addStorePrice = () => {
    setFormData(prev => ({
      ...prev,
      prices: [...prev.prices, { store: 'K-Market', price: 0, shelfNumber: '', storeName: '' }]
    }))
  }

  const updateStorePrice = (index: number, field: 'store' | 'price' | 'shelfNumber' | 'storeName', value: string | number) => {
    setFormData(prev => ({
      ...prev,
      prices: prev.prices.map((price, i) => 
        i === index ? { ...price, [field]: value } : price
      )
    }))
  }

  const removeStorePrice = (index: number) => {
    setFormData(prev => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index)
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEditing ? 'Muokkaa tuotetta' : 'Lisää uusi tuote'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tuotteen nimi *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Esim. Maito"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Hinnat kaupoittain *
                </label>
                <button
                  type="button"
                  onClick={addStorePrice}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Lisää hinta
                </button>
              </div>
              
              {formData.prices.length === 0 ? (
                <div className="border border-gray-200 rounded-md p-4 text-center text-gray-500">
                  <p>Ei hintoja määritetty</p>
                  <button
                    type="button"
                    onClick={addStorePrice}
                    className="mt-2 text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Lisää ensimmäinen hinta
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded-md">
                    <div className="flex-1">Kauppa</div>
                    <div className="w-20 text-center">Hinta</div>
                    <div className="flex-1">Kauppanimi</div>
                    <div className="flex-1">Hyllynumero</div>
                    <div className="w-8"></div>
                  </div>
                  {formData.prices.map((storePrice, index) => (
                    <div key={index} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-md">
                      <div className="flex-1">
                        <select
                          value={storePrice.store}
                          onChange={(e) => updateStorePrice(index, 'store', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          {STORES.filter(store => store !== 'all').map(store => (
                            <option key={store} value={store}>
                              {store}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={storePrice.price}
                          onChange={(e) => updateStorePrice(index, 'price', parseFloat(e.target.value) || 0)}
                          className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="0.00"
                        />
                        <span className="text-sm text-gray-500">€</span>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={storePrice.storeName || ''}
                          onChange={(e) => updateStorePrice(index, 'storeName', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Esim. Pirkka kevytmaito"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={storePrice.shelfNumber || ''}
                          onChange={(e) => updateStorePrice(index, 'shelfNumber', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Hyllynro"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeStorePrice(index)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {CATEGORIES.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kiertoaika (päivää)
              </label>
              <input
                type="number"
                min="1"
                value={formData.rotationDays || ''}
                onChange={(e) => handleChange('rotationDays', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Esim. 7"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isFavorite"
                checked={formData.isFavorite}
                onChange={(e) => handleChange('isFavorite', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isFavorite" className="ml-2 text-sm text-gray-700">
                Suosikki
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Peruuta
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Tallennetaan...' : (isEditing ? 'Tallenna' : 'Lisää')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ProductForm 